/**
 * Single owner of document.title + meta for the SPA.
 * Mount once under Router (App). Do not also set title in AccessibilityEnhancer.
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import seoTable from '../../seo/publicPages.json';
import { alternatesForPath, dirOfLang, langOfPath } from '../../seo/locale';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  ogImage?: string;
  ogType?: string;
  canonicalUrl?: string;
  structuredData?: object;
  /** Force noindex regardless of path defaults */
  noIndex?: boolean;
}

interface PageSEOConfig {
  title: string;
  description: string;
  keywords?: string;
  ogImage?: string;
  ogType?: string;
  structuredData?: object;
  /** App/auth surfaces default to noindex */
  noIndex?: boolean;
}

const SITE = 'https://flahafast.flaha.org';
const BRAND = 'FlahaFAST';
const DEFAULT_OG = `${SITE}/assets/images/flahafast-og-image.png`;
/** Pattern: Page | FlahaFAST — brand always last for tab recognition */
const DEFAULT_TITLE = seoTable.pages[0].title;
const DEFAULT_DESC = seoTable.pages[0].description;

const titleOf = (page: string) => `${page} | ${BRAND}`;

/**
 * Public pages come from the one table that also builds the pre-rendered HTML, the sitemap and robots.txt
 * (src/seo/publicPages.json), so what a visitor's tab says and what a crawler reads cannot drift apart.
 * Sign-in pages are not in the table: they are never indexed.
 */
const PUBLIC_SEO: Record<string, PageSEOConfig> = {
  ...Object.fromEntries(
    seoTable.pages.map((page) => [
      page.path,
      { title: page.title, description: page.description, noIndex: !page.indexable, ogImage: DEFAULT_OG, ogType: 'website' } as PageSEOConfig,
    ]),
  ),
  '/login': {
    title: titleOf('Sign In'),
    description: 'Sign in to your FlahaFAST account for hydroponic nutrient workflows.',
    noIndex: true,
  },
  '/register': {
    title: titleOf('Create Account'),
    description: 'Create a FlahaFAST account and start a 15-day Professional trial.',
    noIndex: true,
  },
  '/forgot-password': {
    title: titleOf('Forgot Password'),
    description: 'Request a password reset link for your FlahaFAST account.',
    noIndex: true,
  },
};

/** Authenticated app — always noindex */
const APP_SEO: Record<string, PageSEOConfig> = {
  '/dashboard': {
    title: titleOf('Workspace'),
    description: 'FlahaFAST workspace — resume your nutrient recipe pipeline.',
    noIndex: true,
  },
  '/upgrade': {
    title: titleOf('Choose your plan'),
    description: 'Plans and checkout for FlahaFAST Professional.',
    noIndex: true,
  },
  '/water-quality': {
    title: titleOf('Water Quality'),
    description: 'Source water analysis for hydroponic nutrient calculations.',
    noIndex: true,
  },
  '/water-profile': {
    title: titleOf('Water Quality'),
    description: 'Source water analysis for hydroponic nutrient calculations.',
    noIndex: true,
  },
  '/formulation': {
    title: titleOf('Crop Formulation'),
    description: 'Crop nutrient targets and formulations.',
    noIndex: true,
  },
  '/formulations': {
    title: titleOf('Formulations'),
    description: 'Manage crop formulations.',
    noIndex: true,
  },
  '/salt-library': {
    title: titleOf('Salt Library'),
    description: 'Nutrient salt selection for your recipe.',
    noIndex: true,
  },
  '/stock-solutions': {
    title: titleOf('Recipe'),
    description: 'Stock solution parameters, solve, and review.',
    noIndex: true,
  },
  '/stock-solution': {
    title: titleOf('Recipe'),
    description: 'Stock solution parameters, solve, and review.',
    noIndex: true,
  },
  '/calculation': {
    title: titleOf('Calculator'),
    description: 'Unified calculation engine.',
    noIndex: true,
  },
  '/results': {
    title: titleOf('Results'),
    description: 'Recipe report and export.',
    noIndex: true,
  },
  '/profile': {
    title: titleOf('Profile'),
    description: 'Account profile and preferences.',
    noIndex: true,
  },
  '/admin': {
    title: titleOf('Admin'),
    description: 'Administration dashboard.',
    noIndex: true,
  },
  '/working-solution': {
    title: titleOf('Working Solution'),
    description: 'Working solution tools.',
    noIndex: true,
  },
  '/nutrient-deficiency': {
    title: titleOf('Nutrient Deficiency'),
    description: 'Deficiency calculator.',
    noIndex: true,
  },
  '/crop-recommendation': {
    title: titleOf('Crop Recommendation'),
    description: 'Crop recommendation tools.',
    noIndex: true,
  },
  '/nutrient-visualization': {
    title: titleOf('Visualization'),
    description: 'Nutrient visualization.',
    noIndex: true,
  },
  '/element-management': {
    title: titleOf('Element Management'),
    description: 'Elements and salts management.',
    noIndex: true,
  },
};

const SEO_CONFIG: Record<string, PageSEOConfig> = {
  ...PUBLIC_SEO,
  ...APP_SEO,
};

function resolveConfig(pathname: string): PageSEOConfig {
  if (SEO_CONFIG[pathname]) return SEO_CONFIG[pathname];
  // Support nested paths
  if (pathname.startsWith('/support/')) {
    return (
      SEO_CONFIG[pathname] || {
        title: titleOf('Support'),
        description: DEFAULT_DESC,
      }
    );
  }
  // Verify email tokens
  if (pathname.startsWith('/verify-email')) {
    return {
      title: titleOf('Verify Email'),
      description: 'Confirm your FlahaFAST account email address.',
      noIndex: true,
    };
  }
  if (pathname.startsWith('/reset-password')) {
    return {
      title: titleOf('Reset Password'),
      description: 'Set a new password for your FlahaFAST account.',
      noIndex: true,
    };
  }
  // Default: treat unknown as app (noindex)
  return {
    title: DEFAULT_TITLE,
    description: DEFAULT_DESC,
    noIndex: true,
  };
}

/** Replace the page's hreflang links with these (none, when the page has no translation). */
function updateHreflang(alternates: { lang: string; url: string }[]) {
  document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());
  for (const a of alternates) {
    const link = document.createElement('link');
    link.rel = 'alternate';
    link.hreflang = a.lang;
    link.href = a.url;
    document.head.appendChild(link);
  }
}

const SEOHead: React.FC<SEOProps> = ({
  title,
  description,
  keywords,
  ogImage,
  ogType = 'website',
  canonicalUrl,
  structuredData,
  noIndex,
}) => {
  const location = useLocation();
  const pageConfig = resolveConfig(location.pathname);

  const finalTitle = title || pageConfig.title || DEFAULT_TITLE;
  const finalDescription =
    description || pageConfig.description || DEFAULT_DESC;
  const finalOgImage = ogImage || pageConfig.ogImage || DEFAULT_OG;
  const finalOgType = ogType || pageConfig.ogType || 'website';
  const finalCanonicalUrl =
    canonicalUrl || `${SITE}${location.pathname === '/' ? '/' : location.pathname}`;
  const finalStructuredData = structuredData || pageConfig.structuredData;
  const finalNoIndex =
    noIndex === true || pageConfig.noIndex === true;

  useEffect(() => {
    document.title = finalTitle;

    updateMetaTag('description', finalDescription);
    updateMetaTag(
      'robots',
      finalNoIndex ? 'noindex, nofollow' : 'index, follow'
    );

    updateMetaProperty('og:title', finalTitle);
    updateMetaProperty('og:site_name', BRAND);
    updateMetaProperty('og:description', finalDescription);
    updateMetaProperty('og:image', finalOgImage);
    updateMetaProperty('og:type', finalOgType);
    updateMetaProperty('og:url', finalCanonicalUrl);

    updateMetaProperty('twitter:title', finalTitle);
    updateMetaProperty('twitter:description', finalDescription);
    updateMetaProperty('twitter:image', finalOgImage);

    updateCanonicalUrl(finalCanonicalUrl);

    // Language: Arabic public pages are right to left and point at their English twin (and back) with hreflang.
    const pageLang = langOfPath(location.pathname);
    document.documentElement.lang = pageLang;
    document.documentElement.dir = dirOfLang(pageLang);
    updateHreflang(alternatesForPath(location.pathname));

    if (finalStructuredData) {
      updateStructuredData(finalStructuredData);
    } else {
      removePageStructuredData();
    }

    if (typeof gtag !== 'undefined') {
      gtag('config', 'G-YX5KE78HRV', {
        page_title: finalTitle,
        page_location: finalCanonicalUrl,
      });
    }
  }, [
    location.pathname,
    finalTitle,
    finalDescription,
    finalOgImage,
    finalOgType,
    finalCanonicalUrl,
    finalStructuredData,
    finalNoIndex,
  ]);

  return null;
};

const updateMetaTag = (name: string, content: string) => {
  let element = document.querySelector(
    `meta[name="${name}"]`
  ) as HTMLMetaElement | null;
  if (!element) {
    element = document.createElement('meta');
    element.name = name;
    document.head.appendChild(element);
  }
  element.content = content;
};

const updateMetaProperty = (property: string, content: string) => {
  let element = document.querySelector(
    `meta[property="${property}"]`
  ) as HTMLMetaElement | null;
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute('property', property);
    document.head.appendChild(element);
  }
  element.content = content;
};

const updateCanonicalUrl = (url: string) => {
  let element = document.querySelector(
    'link[rel="canonical"]'
  ) as HTMLLinkElement | null;
  if (!element) {
    element = document.createElement('link');
    element.rel = 'canonical';
    document.head.appendChild(element);
  }
  element.href = url;
};

const updateStructuredData = (data: object) => {
  removePageStructuredData();
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.setAttribute('data-page-structured-data', 'true');
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
};

const removePageStructuredData = () => {
  document
    .querySelectorAll('script[data-page-structured-data]')
    .forEach((el) => el.remove());
};

export default SEOHead;
